import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  MapPin, 
  Search, 
  Package, 
  Calendar, 
  Handshake, 
  User, 
  Clock, 
  AlertTriangle,
  RefreshCw,
  Eye,
  FileDown,
  Settings,
  Bell,
  Mail,
  Plus,
  Trash2,
  Play,
  Info,
  Hash,
  RotateCcw,
  CheckCircle
} from 'lucide-react';
import { formatDate } from '../lib/utils';

const EquipmentTrackingPage = () => {
  const { api, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('events');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeStatFilter, setActiveStatFilter] = useState(null); // 'events', 'loans', 'overdue', 'due_soon', 'maintenance', 'faults', 'lost'
  
  // Data states
  const [eventEquipment, setEventEquipment] = useState([]);
  const [loanEquipment, setLoanEquipment] = useState([]);
  const [stats, setStats] = useState({
    totalInEvents: 0,
    totalOnLoan: 0,
    overdueLoans: 0,
    dueSoon: 0,
    maintenance: 0,
    faults: 0,
    lost: 0,
    lostRecover: 0,
    lostDefinitive: 0
  });
  
  // Incident history states
  const [incidentHistory, setIncidentHistory] = useState([]);
  // v5.4.5 — track in-flight recovery to disable individual buttons
  const [recoveringUnitId, setRecoveringUnitId] = useState(null);
  const [loadingIncidents, setLoadingIncidents] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [incidentModalOpen, setIncidentModalOpen] = useState(false);
  
  // Alert config states
  const [alertConfig, setAlertConfig] = useState({
    enabled: true,
    check_time: '09:00',
    recipients: [],
    notify_days_before: [3, 7],
    notify_overdue: true
  });
  const [alertStatus, setAlertStatus] = useState(null);
  const [newRecipientEmail, setNewRecipientEmail] = useState('');
  const [newRecipientName, setNewRecipientName] = useState('');
  const [savingConfig, setSavingConfig] = useState(false);
  
  // Movement details modal state
  const [selectedMovement, setSelectedMovement] = useState(null);
  const [movementDetailsOpen, setMovementDetailsOpen] = useState(false);
  const [movementFullData, setMovementFullData] = useState(null);
  
  // Return loan modal state
  const [returnModalOpen, setReturnModalOpen] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [returnData, setReturnData] = useState({
    notes: '',
    items: []
  });
  const [processingReturn, setProcessingReturn] = useState(false);
  const [locations, setLocations] = useState([]);
  
  // Bomberazo alert config states
  const [bomberazoConfig, setBomberazoConfig] = useState({
    enabled: false,
    notify_user_ids: [],
    send_email: true,
    send_notification: true,
    daily_report_enabled: true,
    daily_report_time: '08:00',
    daily_report_recipients: []
  });
  const [allUsers, setAllUsers] = useState([]);
  const [newBomberazoEmail, setNewBomberazoEmail] = useState('');
  const [savingBomberazoConfig, setSavingBomberazoConfig] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const apiInstance = api();
      
      // Get all active salidas (events) without return, plus status counts
      const [movementsRes, loansRes, kpisRes, statusCountsRes] = await Promise.all([
        apiInstance.get('/movements'),
        apiInstance.get('/dashboard/active-loans'),
        apiInstance.get('/dashboard/kpis'),
        apiInstance.get('/inventory/status-counts').catch(() => ({ data: null }))
      ]);

      // Filter salidas that are events (not loans) and pending return
      const allMovements = movementsRes.data || [];
      const activeSalidas = allMovements.filter(m => 
        m.type === 'salida' && !m.has_return
      );
      
      // Get equipment details for each salida
      const eventEquipmentList = [];
      for (const salida of activeSalidas) {
        const items = salida.items || [];
        for (const item of items) {
          eventEquipmentList.push({
            movement_id: salida.id,
            event_name: salida.event_name,
            client_name: salida.client_name,
            event_date: salida.event_date,
            event_time: salida.event_time,
            responsible_name: salida.responsible_name,
            equipment_name: item.equipment_name || 'N/A',
            serial_number: item.serial_numbers || 'Auto-asignado',
            quantity: item.quantity,
            created_at: salida.created_at || salida.date,
            control_number: salida.control_number
          });
        }
      }
      
      setEventEquipment(eventEquipmentList);
      setLoanEquipment(loansRes.data || []);
      
      // Calculate stats including incidents from status counts
      const loans = loansRes.data || [];
      const statusCounts = statusCountsRes.data || {};
      
      setStats({
        totalInEvents: eventEquipmentList.reduce((sum, e) => sum + (e.quantity || 1), 0),
        totalOnLoan: kpisRes.data?.on_loan || 0,
        overdueLoans: loans.filter(l => l.is_overdue).length,
        dueSoon: loans.filter(l => !l.is_overdue && l.days_until_due !== null && l.days_until_due <= 7).length,
        maintenance: statusCounts.mantenimiento || 0,
        faults: statusCounts.falla || 0,
        lost: statusCounts.perdido || 0,
        // v5.4.2 — sub-buckets for the Perdidos card mini-breakdown
        lostRecover: statusCounts.perdido_recuperar || 0,
        lostDefinitive: statusCounts.perdido_definitivo || 0
      });
      
      // Load alert config
      try {
        const [configRes, statusRes] = await Promise.all([
          apiInstance.get('/loan-alerts/config'),
          apiInstance.get('/loan-alerts/status')
        ]);
        setAlertConfig(configRes.data);
        setAlertStatus(statusRes.data);
      } catch (e) {
        console.error('Error loading alert config:', e);
      }
      
    } catch (error) {
      toast.error('Error al cargar datos de rastreo');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Load incident history for a specific status
  const loadIncidentHistory = useCallback(async (status) => {
    try {
      setLoadingIncidents(true);
      const response = await api().get(`/equipment/incident-history/${status}`);
      setIncidentHistory(response.data.units || []);
    } catch (error) {
      console.error('Error loading incident history:', error);
      toast.error('Error al cargar historial de incidentes');
      setIncidentHistory([]);
    } finally {
      setLoadingIncidents(false);
    }
  }, [api]);

  // v5.4.5 — Mark a unit as recovered (Perdido → Disponible)
  const handleRecoverUnit = async (incident) => {
    const label = incident.equipment_name && incident.serial_number
      ? `${incident.equipment_name} (${incident.serial_number})`
      : incident.unit_id;
    if (!window.confirm(`¿Marcar como recuperada la unidad ${label}? Se moverá a 'Disponible' y quedará registrada en el historial.`)) {
      return;
    }
    setRecoveringUnitId(incident.unit_id);
    try {
      const res = await api().post(`/equipment-units/${incident.unit_id}/recover`, { target_status: 'disponible' });
      toast.success(`Recuperado: ${label} → Disponible`);
      // Remove from current list immediately for snappy UX
      setIncidentHistory((prev) => prev.filter((i) => i.unit_id !== incident.unit_id));
      // Refresh global stats
      loadData();
      return res.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo recuperar la unidad');
    } finally {
      setRecoveringUnitId(null);
    }
  };

  // Handle stat card click - load incidents for specific status
  const handleStatCardClick = (filterType) => {
    if (activeStatFilter === filterType) {
      setActiveStatFilter(null);
      setIncidentHistory([]);
    } else {
      setActiveStatFilter(filterType);
      // Load incident history for maintenance, faults, or lost
      if (['maintenance', 'faults', 'lost'].includes(filterType)) {
        const statusMap = {
          'maintenance': 'mantenimiento',
          'faults': 'falla',
          'lost': 'perdido'
        };
        loadIncidentHistory(statusMap[filterType]);
      } else {
        setIncidentHistory([]);
      }
    }
  };

  useEffect(() => {
    loadData();
  }, [loadData]);
  
  // Load bomberazo config and users
  const loadBomberazoConfig = useCallback(async () => {
    try {
      const [configRes, usersRes] = await Promise.all([
        api().get('/bomberazo-alerts/config'),
        api().get('/users')
      ]);
      setBomberazoConfig(configRes.data);
      setAllUsers(usersRes.data || []);
    } catch (error) {
      console.error('Error loading bomberazo config:', error);
    }
  }, [api]);
  
  useEffect(() => {
    loadBomberazoConfig();
  }, [loadBomberazoConfig]);
  
  // Bomberazo alert functions
  const toggleBomberazoUser = (userId) => {
    setBomberazoConfig(prev => {
      const newIds = prev.notify_user_ids.includes(userId)
        ? prev.notify_user_ids.filter(id => id !== userId)
        : [...prev.notify_user_ids, userId];
      return { ...prev, notify_user_ids: newIds };
    });
  };
  
  const addBomberazoReportEmail = () => {
    if (!newBomberazoEmail || !newBomberazoEmail.includes('@')) {
      toast.error('Email inválido');
      return;
    }
    setBomberazoConfig(prev => ({
      ...prev,
      daily_report_recipients: [...prev.daily_report_recipients, newBomberazoEmail]
    }));
    setNewBomberazoEmail('');
  };
  
  const removeBomberazoReportEmail = (index) => {
    setBomberazoConfig(prev => ({
      ...prev,
      daily_report_recipients: prev.daily_report_recipients.filter((_, i) => i !== index)
    }));
  };
  
  const saveBomberazoConfig = async () => {
    try {
      setSavingBomberazoConfig(true);
      await api().post('/bomberazo-alerts/config', bomberazoConfig);
      // Schedule the daily report
      await api().post('/bomberazo-alerts/schedule');
      toast.success('Configuración de bomberazos guardada');
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setSavingBomberazoConfig(false);
    }
  };
  
  const testBomberazoReport = async () => {
    try {
      await api().post('/bomberazo-alerts/test-report');
      toast.success('Reporte de bomberazos ejecutado');
    } catch (error) {
      toast.error('Error al ejecutar reporte');
    }
  };
  
  // Handle view movement details
  const handleViewMovementDetails = async (item) => {
    try {
      setSelectedMovement(item);
      setMovementDetailsOpen(true);
      // Fetch full movement data
      const response = await api().get(`/movements/${item.movement_id}`);
      setMovementFullData(response.data);
    } catch (error) {
      console.error('Error loading movement details:', error);
      toast.error('Error al cargar detalles del movimiento');
    }
  };
  
  // Load locations for return modal
  const loadLocations = async () => {
    try {
      const response = await api().get('/locations');
      setLocations(response.data || []);
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };
  
  useEffect(() => {
    loadLocations();
  }, []);
  
  // Handle open return modal for loan
  const handleOpenReturnModal = async (loan) => {
    try {
      // Get full movement details
      const response = await api().get(`/movements/${loan.id}`);
      const movementData = response.data;
      
      setSelectedLoan(loan);
      setReturnData({
        notes: '',
        items: (movementData.items || []).map(item => ({
          ...item,
          return_quantity: item.quantity,
          return_condition: 'BUENO',
          return_location_id: item.location_id || '',
          notes: ''
        }))
      });
      setReturnModalOpen(true);
    } catch (error) {
      console.error('Error loading loan details:', error);
      toast.error('Error al cargar detalles del préstamo');
    }
  };
  
  // Handle item condition change
  const handleItemConditionChange = (index, field, value) => {
    setReturnData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };
  
  // Process return
  const handleProcessReturn = async () => {
    if (!selectedLoan) return;
    
    try {
      setProcessingReturn(true);
      
      // Prepare return data
      const returnPayload = {
        salida_id: selectedLoan.id,
        responsible_id: selectedLoan.responsible_id,
        notes: returnData.notes,
        items: returnData.items.map(item => ({
          equipment_id: item.equipment_id,
          quantity: item.return_quantity,
          condition: item.return_condition,
          location_id: item.return_location_id,
          fault_reason: item.return_condition === 'FALLA' ? item.notes : null,
          serial_numbers: item.serial_numbers
        }))
      };
      
      // Create return movement
      await api().post('/movements/returns', returnPayload);
      
      toast.success('Retorno registrado correctamente');
      setReturnModalOpen(false);
      setSelectedLoan(null);
      
      // Reload data
      loadData();
      
    } catch (error) {
      console.error('Error processing return:', error);
      toast.error(error.response?.data?.detail || 'Error al procesar retorno');
    } finally {
      setProcessingReturn(false);
    }
  };
  
  // Alert config functions
  const addRecipient = () => {
    if (!newRecipientEmail) {
      toast.error('Ingresa un email');
      return;
    }
    if (!newRecipientEmail.includes('@')) {
      toast.error('Email inválido');
      return;
    }
    
    setAlertConfig(prev => ({
      ...prev,
      recipients: [...prev.recipients, { email: newRecipientEmail, name: newRecipientName || newRecipientEmail }]
    }));
    setNewRecipientEmail('');
    setNewRecipientName('');
  };
  
  const removeRecipient = (index) => {
    setAlertConfig(prev => ({
      ...prev,
      recipients: prev.recipients.filter((_, i) => i !== index)
    }));
  };
  
  const saveAlertConfig = async () => {
    try {
      setSavingConfig(true);
      await api().put('/loan-alerts/config', alertConfig);
      toast.success('Configuración guardada');
      
      // Reload status
      const statusRes = await api().get('/loan-alerts/status');
      setAlertStatus(statusRes.data);
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setSavingConfig(false);
    }
  };
  
  const testAlerts = async () => {
    try {
      await api().post('/loan-alerts/test');
      toast.success('Verificación ejecutada - revisa el Debugger para ver los resultados');
    } catch (error) {
      toast.error('Error al ejecutar verificación');
    }
  };

  // v5.4.8 — Send sample PDF to validate digest email + attachment pipeline
  const sendTestLoanPdfEmail = async () => {
    const email = window.prompt('Enviar PDF de prueba (Préstamos) a:\n\nEscribe el correo destinatario para validar la entrega del adjunto.', '');
    if (!email) return;
    try {
      const res = await api().post('/email/test-loan-pdf', { recipient_email: email.trim() });
      toast.success(`PDF enviado a ${res.data.recipient} vía ${res.data.provider}. Revisa tu bandeja.`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Error al enviar PDF de prueba');
    }
  };

  const sendTestBomberazoPdfEmail = async () => {
    const email = window.prompt('Enviar PDF de prueba (Bomberazos) a:\n\nEscribe el correo destinatario para validar la entrega del adjunto.', '');
    if (!email) return;
    try {
      const res = await api().post('/email/test-bomberazo-pdf', { recipient_email: email.trim() });
      toast.success(`PDF enviado a ${res.data.recipient} vía ${res.data.provider}. Revisa tu bandeja.`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Error al enviar PDF de prueba');
    }
  };

  // Filter equipment by search term and stat filter
  const filteredEventEquipment = eventEquipment.filter(item => {
    // First apply search filter
    const matchesSearch = 
      item.event_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.responsible_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.equipment_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.serial_number?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    // Apply stat filter if active
    if (activeStatFilter === 'events') return true; // Show all events
    if (activeStatFilter && activeStatFilter !== 'events') return false; // Hide events when filtering loans
    
    return true;
  });

  const filteredLoanEquipment = loanEquipment.filter(item => {
    // First apply search filter
    const matchesSearch = 
      item.loan_reason?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.responsible_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    // Apply stat filter if active
    if (activeStatFilter === 'loans') return true; // Show all loans
    if (activeStatFilter === 'overdue') return item.is_overdue === true; // Only overdue
    if (activeStatFilter === 'due_soon') {
      // Due within 7 days but not overdue
      return !item.is_overdue && item.days_until_due !== null && item.days_until_due <= 7;
    }
    if (activeStatFilter === 'events') return false; // Hide loans when filtering events
    
    return true;
  });

  const StatCard = ({ icon: Icon, label, value, color, subtext, onClick, isActive }) => (
    <Card 
      className={`cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg ${isActive ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : ''}`}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${color}`}>
            <Icon className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-xs text-muted-foreground">{label}</p>
            {subtext && <p className="text-xs text-muted-foreground">{subtext}</p>}
          </div>
        </div>
        {isActive && (
          <div className="mt-2 text-xs text-primary font-medium">
            ✓ Filtro activo
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="p-6">
          <div className="flex items-center gap-3 text-muted-foreground">
            <AlertTriangle className="h-5 w-5" />
            <p>No tienes permisos para ver esta página</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="equipment-tracking-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MapPin className="h-6 w-6 text-primary" />
            Rastreo de Equipo
          </h1>
          <p className="text-muted-foreground">Monitorea todo el equipo que está fuera de inventario</p>
        </div>
        <Button onClick={loadData} variant="outline" disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Stats Cards - Clickeable filters */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        <StatCard
          icon={Calendar}
          label="En Eventos"
          value={stats.totalInEvents}
          color="bg-primary"
          isActive={activeStatFilter === 'events'}
          onClick={() => {
            if (activeStatFilter === 'events') {
              setActiveStatFilter(null);
            } else {
              setActiveStatFilter('events');
              setActiveTab('events');
              setIncidentHistory([]);
            }
          }}
        />
        <StatCard
          icon={Handshake}
          label="En Préstamo"
          value={stats.totalOnLoan}
          color="bg-purple-500"
          isActive={activeStatFilter === 'loans'}
          onClick={() => {
            if (activeStatFilter === 'loans') {
              setActiveStatFilter(null);
            } else {
              setActiveStatFilter('loans');
              setActiveTab('loans');
              setIncidentHistory([]);
            }
          }}
        />
        <StatCard
          icon={AlertTriangle}
          label="Préstamos Vencidos"
          value={stats.overdueLoans}
          color="bg-red-500"
          isActive={activeStatFilter === 'overdue'}
          onClick={() => {
            if (activeStatFilter === 'overdue') {
              setActiveStatFilter(null);
            } else {
              setActiveStatFilter('overdue');
              setActiveTab('loans');
              setIncidentHistory([]);
            }
          }}
        />
        <StatCard
          icon={Clock}
          label="Por Vencer (7 días)"
          value={stats.dueSoon}
          color="bg-amber-500"
          isActive={activeStatFilter === 'due_soon'}
          onClick={() => {
            if (activeStatFilter === 'due_soon') {
              setActiveStatFilter(null);
            } else {
              setActiveStatFilter('due_soon');
              setActiveTab('loans');
              setIncidentHistory([]);
            }
          }}
        />
        {/* New incident stat cards */}
        <StatCard
          icon={Settings}
          label="Mantenimiento"
          value={stats.maintenance}
          color="bg-amber-600"
          isActive={activeStatFilter === 'maintenance'}
          onClick={() => handleStatCardClick('maintenance')}
        />
        <StatCard
          icon={AlertTriangle}
          label="Con Fallas"
          value={stats.faults}
          color="bg-orange-500"
          isActive={activeStatFilter === 'faults'}
          onClick={() => handleStatCardClick('faults')}
        />
        <StatCard
          icon={Info}
          label="Perdidos"
          value={stats.lost}
          subtext={stats.lost > 0 ? `${stats.lostRecover} a recuperar · ${stats.lostDefinitive} definitivos` : null}
          color="bg-red-600"
          isActive={activeStatFilter === 'lost'}
          onClick={() => handleStatCardClick('lost')}
        />
      </div>
      
      {/* Active filter indicator */}
      {activeStatFilter && (
        <div className="flex items-center gap-2 p-3 bg-primary/10 border border-primary/30 rounded-lg">
          <span className="text-sm">Filtrando por: <strong>
            {activeStatFilter === 'events' && 'Equipos en Eventos'}
            {activeStatFilter === 'loans' && 'Equipos en Préstamo'}
            {activeStatFilter === 'overdue' && 'Préstamos Vencidos'}
            {activeStatFilter === 'due_soon' && 'Por Vencer (7 días)'}
            {activeStatFilter === 'maintenance' && 'Equipos en Mantenimiento'}
            {activeStatFilter === 'faults' && 'Equipos con Fallas'}
            {activeStatFilter === 'lost' && 'Equipos Perdidos'}
          </strong></span>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => {
              setActiveStatFilter(null);
              setIncidentHistory([]);
            }}
            className="ml-auto h-6 px-2"
          >
            Limpiar filtro
          </Button>
        </div>
      )}

      {/* Incident History Panel - Shows when maintenance/faults/lost filter is active */}
      {['maintenance', 'faults', 'lost'].includes(activeStatFilter) && (
        <Card className="border-l-4 border-l-orange-500">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              {activeStatFilter === 'maintenance' && <Settings className="h-5 w-5 text-amber-600" />}
              {activeStatFilter === 'faults' && <AlertTriangle className="h-5 w-5 text-orange-500" />}
              {activeStatFilter === 'lost' && <Info className="h-5 w-5 text-red-600" />}
              Historial de Incidentes - {
                activeStatFilter === 'maintenance' ? 'Equipos en Mantenimiento' :
                activeStatFilter === 'faults' ? 'Equipos con Fallas' :
                'Equipos Perdidos'
              }
            </CardTitle>
            <CardDescription>
              Detalle de qué pasó, dónde y por qué con cada equipo
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingIncidents ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : incidentHistory.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No hay equipos con este estado</p>
              </div>
            ) : (
              <div className="space-y-3">
                {incidentHistory.map((incident, idx) => (
                  <div 
                    key={incident.unit_id || idx}
                    className="p-4 border rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => {
                      setSelectedIncident(incident);
                      setIncidentModalOpen(true);
                    }}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">{incident.equipment_name}</span>
                          <Badge variant="outline" className="text-xs">
                            {incident.serial_number}
                          </Badge>
                          {/* v5.4.3 — derive badge label/color from the active filter context
                              so that legacy status string variants (e.g. "perdido_a_recuperar",
                              uppercase variants, etc.) don't fall through to the amber default. */}
                          {(() => {
                            const status = (incident.status || '').toLowerCase();
                            let label, bg;
                            if (activeStatFilter === 'lost' ||
                                ['perdido', 'faltante', 'perdido_recuperar', 'perdido_definitivo'].includes(status) ||
                                status.includes('perdid') || status.includes('faltant')) {
                              bg = 'bg-red-600';
                              label = status === 'perdido_recuperar' ? 'Perdido (a recuperar)' :
                                      status === 'perdido_definitivo' ? 'Perdido definitivo' :
                                      'Perdido';
                            } else if (activeStatFilter === 'faults' ||
                                       ['falla', 'con_falla'].includes(status) ||
                                       status.includes('falla')) {
                              bg = 'bg-orange-500';
                              label = 'Con Falla';
                            } else {
                              bg = 'bg-amber-600';
                              label = status === 'mantenimiento_interno' ? 'Mantenimiento interno' :
                                      status === 'mantenimiento_externo' ? 'Mantenimiento externo' :
                                      'Mantenimiento';
                            }
                            return <Badge className={`text-xs ${bg}`}>{label}</Badge>;
                          })()}
                        </div>
                        
                        {incident.fault_reason && (
                          <p className="text-sm text-muted-foreground mb-2">
                            <strong>Razón:</strong> {incident.fault_reason}
                          </p>
                        )}
                        
                        {incident.incident_details ? (
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            {incident.incident_details.event_name && (
                              <div>
                                <span className="text-muted-foreground">Evento:</span>{' '}
                                {incident.incident_details.movement_id ? (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/movements?open=${incident.incident_details.movement_id}`);
                                    }}
                                    className="font-medium text-primary hover:underline cursor-pointer"
                                    data-testid={`incident-event-link-${incident.unit_id}`}
                                  >
                                    {incident.incident_details.event_name} ↗
                                  </button>
                                ) : (
                                  <span className="font-medium">{incident.incident_details.event_name}</span>
                                )}
                              </div>
                            )}
                            {incident.incident_details.event_date && (
                              <div>
                                <span className="text-muted-foreground">Fecha:</span>{' '}
                                <span>{formatDate(incident.incident_details.event_date)}</span>
                              </div>
                            )}
                            {incident.incident_details.client_name && (
                              <div>
                                <span className="text-muted-foreground">Cliente:</span>{' '}
                                <span>{incident.incident_details.client_name}</span>
                              </div>
                            )}
                            {incident.incident_details.responsible_name && (
                              <div>
                                <span className="text-muted-foreground">Responsable:</span>{' '}
                                <span className="font-medium text-blue-400">{incident.incident_details.responsible_name}</span>
                              </div>
                            )}
                            {incident.incident_details.registered_by_name && (
                              <div>
                                <span className="text-muted-foreground">Registrado por:</span>{' '}
                                <span className="font-medium text-amber-400">{incident.incident_details.registered_by_name}</span>
                              </div>
                            )}
                            {incident.incident_details.notes && (
                              <div className="col-span-2">
                                <span className="text-muted-foreground">Notas:</span>{' '}
                                <span>{incident.incident_details.notes}</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground italic">
                            Sin información del incidente original
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <Eye className="h-4 w-4 text-muted-foreground" />
                        {activeStatFilter === 'lost' && isAdmin && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs h-7 border-emerald-500/40 text-emerald-500 hover:bg-emerald-500/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRecoverUnit(incident);
                            }}
                            disabled={recoveringUnitId === incident.unit_id}
                            data-testid={`recover-unit-btn-${incident.unit_id}`}
                          >
                            {recoveringUnitId === incident.unit_id ? (
                              <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                            ) : (
                              <CheckCircle className="h-3 w-3 mr-1" />
                            )}
                            Marcar recuperado
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por evento, responsable, equipo, serial..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Tabs - Using manual implementation for reliability */}
      <div className="w-full">
        <div className="inline-flex h-10 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground gap-1 w-full max-w-lg">
          <button
            type="button"
            onClick={() => setActiveTab('events')}
            className={`flex-1 inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all cursor-pointer ${
              activeTab === 'events' 
                ? 'bg-background text-foreground shadow-sm' 
                : 'hover:bg-background/50'
            }`}
            data-testid="tab-events"
          >
            <Calendar className="h-4 w-4 mr-2" />
            En Evento ({filteredEventEquipment.length})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('loans')}
            className={`flex-1 inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all cursor-pointer ${
              activeTab === 'loans' 
                ? 'bg-background text-foreground shadow-sm' 
                : 'hover:bg-background/50'
            }`}
            data-testid="tab-loans"
          >
            <Handshake className="h-4 w-4 mr-2" />
            En Préstamo ({filteredLoanEquipment.length})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('alerts')}
            className={`flex-1 inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all cursor-pointer ${
              activeTab === 'alerts' 
                ? 'bg-background text-foreground shadow-sm' 
                : 'hover:bg-background/50'
            }`}
            data-testid="tab-alerts"
          >
            <Bell className="h-4 w-4 mr-2" />
            Alertas
          </button>
        </div>

        {/* Events Tab Content */}
        {activeTab === 'events' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                Equipo en Eventos
              </CardTitle>
              <CardDescription>
                Equipos que están actualmente en eventos y pendientes de retorno
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : filteredEventEquipment.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No hay equipos en eventos actualmente</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Equipo</TableHead>
                        <TableHead>Serial</TableHead>
                        <TableHead>No. Control</TableHead>
                        <TableHead>Evento</TableHead>
                        <TableHead>Responsable</TableHead>
                        <TableHead>Fecha Evento</TableHead>
                        <TableHead>Días Fuera</TableHead>
                        <TableHead className="text-center">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredEventEquipment.map((item, idx) => {
                        const daysOut = item.created_at 
                          ? Math.floor((new Date() - new Date(item.created_at)) / (1000 * 60 * 60 * 24))
                          : 0;
                        return (
                          <TableRow key={idx} className="cursor-pointer hover:bg-muted/50" onClick={() => handleViewMovementDetails(item)}>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <Package className="h-4 w-4 text-primary" />
                                {item.equipment_name}
                                {item.quantity > 1 && (
                                  <Badge variant="outline" className="ml-1">x{item.quantity}</Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="font-mono text-sm">{item.serial_number}</TableCell>
                            <TableCell className="font-mono text-xs">
                              {item.control_number || '-'}
                            </TableCell>
                            <TableCell>
                              <span className="font-medium">{item.event_name}</span>
                              <span className="text-xs text-muted-foreground block">{item.client_name}</span>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {item.responsible_name}
                              </div>
                            </TableCell>
                            <TableCell>
                              {item.event_date ? formatDate(item.event_date) : '-'}
                              {item.event_time && <span className="text-xs ml-1">{item.event_time}</span>}
                            </TableCell>
                            <TableCell>
                              <Badge variant={daysOut > 7 ? 'destructive' : daysOut > 3 ? 'warning' : 'default'}>
                                {daysOut} días
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={(e) => { e.stopPropagation(); handleViewMovementDetails(item); }}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Loans Tab Content */}
        {activeTab === 'loans' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Handshake className="h-5 w-5 text-purple-500" />
                Equipo en Préstamo
              </CardTitle>
              <CardDescription>
                Equipos prestados a largo plazo con fechas de devolución
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : filteredLoanEquipment.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Handshake className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No hay equipos en préstamo actualmente</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Motivo</TableHead>
                        <TableHead>Responsable</TableHead>
                        <TableHead>Equipos</TableHead>
                        <TableHead>Fecha Préstamo</TableHead>
                        <TableHead>Fecha Devolución</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead>Recordatorios</TableHead>
                        <TableHead className="text-center">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredLoanEquipment.map((loan) => (
                        <TableRow key={loan.id} className={loan.is_overdue ? 'bg-red-500/10' : ''}>
                          <TableCell className="font-medium max-w-[200px]">
                            <span className="truncate block" title={loan.loan_reason}>
                              {loan.loan_reason || 'Préstamo'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {loan.responsible_name}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{loan.total_items} equipo(s)</Badge>
                          </TableCell>
                          <TableCell>{loan.date ? formatDate(loan.date) : '-'}</TableCell>
                          <TableCell>
                            {loan.is_indefinite ? (
                              <span className="text-muted-foreground">Indefinido</span>
                            ) : loan.loan_due_date ? (
                              <span className={loan.is_overdue ? 'text-red-500 font-medium' : ''}>
                                {formatDate(loan.loan_due_date)}
                              </span>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell>
                            {loan.is_overdue ? (
                              <Badge className="bg-red-500/20 text-red-400">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                Vencido
                              </Badge>
                            ) : loan.is_indefinite ? (
                              <Badge className="bg-gray-500/20 text-gray-400">Indefinido</Badge>
                            ) : loan.days_until_due !== null && loan.days_until_due <= 7 ? (
                              <Badge className="bg-amber-500/20 text-amber-400">
                                <Clock className="h-3 w-3 mr-1" />
                                {loan.days_until_due} días
                              </Badge>
                            ) : (
                              <Badge className="bg-purple-500/20 text-purple-400">Activo</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {loan.reminders_enabled ? (
                              <Badge variant="outline" className="text-amber-500 border-amber-500">
                                Activos
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground text-sm">No</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <Button 
                              variant="default" 
                              size="sm" 
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleOpenReturnModal(loan)}
                              data-testid={`return-loan-${loan.id}`}
                            >
                              <RotateCcw className="h-4 w-4 mr-1" />
                              Retornar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Alerts Configuration Tab Content */}
        {activeTab === 'alerts' && (
          <div className="space-y-6 mt-2">
            <div className="grid gap-6 lg:grid-cols-2">
              {/* Alert Settings */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Settings className="h-5 w-5" />
                    Configuración de Alertas
                  </CardTitle>
                <CardDescription>
                  Configura las alertas automáticas de préstamos vencidos
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="alerts_enabled"
                    checked={alertConfig.enabled}
                    onCheckedChange={(checked) => setAlertConfig(prev => ({ ...prev, enabled: checked }))}
                  />
                  <Label htmlFor="alerts_enabled" className="cursor-pointer">
                    Activar verificación automática de préstamos
                  </Label>
                </div>
                
                {alertConfig.enabled && (
                  <>
                    <div className="space-y-2">
                      <Label>Hora de verificación diaria</Label>
                      <Input
                        type="time"
                        value={alertConfig.check_time}
                        onChange={(e) => setAlertConfig(prev => ({ ...prev, check_time: e.target.value }))}
                        className="w-40"
                      />
                      <p className="text-xs text-muted-foreground">
                        Cada día a esta hora se verificarán los préstamos
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          id="notify_overdue"
                          checked={alertConfig.notify_overdue}
                          onCheckedChange={(checked) => setAlertConfig(prev => ({ ...prev, notify_overdue: checked }))}
                        />
                        <Label htmlFor="notify_overdue" className="cursor-pointer">
                          Notificar préstamos vencidos
                        </Label>
                      </div>
                    </div>
                  </>
                )}
                
                {alertStatus && (
                  <div className="p-3 bg-muted/30 rounded-lg text-sm">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${alertStatus.job_exists ? 'bg-green-500' : 'bg-red-500'}`} />
                      <span>{alertStatus.job_exists ? 'Job activo' : 'Job inactivo'}</span>
                    </div>
                    {alertStatus.next_run && (
                      <p className="text-muted-foreground mt-1">
                        Próxima verificación: {new Date(alertStatus.next_run).toLocaleString('es-MX')}
                      </p>
                    )}
                  </div>
                )}
                
                <div className="flex gap-2">
                  <Button onClick={saveAlertConfig} disabled={savingConfig}>
                    {savingConfig ? 'Guardando...' : 'Guardar Configuración'}
                  </Button>
                  <Button variant="outline" onClick={testAlerts}>
                    <Play className="h-4 w-4 mr-2" />
                    Probar Ahora
                  </Button>
                  <Button variant="outline" onClick={sendTestLoanPdfEmail} data-testid="test-loan-pdf-email-btn">
                    <Mail className="h-4 w-4 mr-2" />
                    Probar PDF por correo
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Recipients */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Destinatarios de Alertas
                </CardTitle>
                <CardDescription>
                  Personas que recibirán las alertas de préstamos vencidos
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Add recipient form */}
                <div className="flex gap-2">
                  <Input
                    placeholder="Nombre"
                    value={newRecipientName}
                    onChange={(e) => setNewRecipientName(e.target.value)}
                    className="w-32"
                  />
                  <Input
                    placeholder="Email"
                    type="email"
                    value={newRecipientEmail}
                    onChange={(e) => setNewRecipientEmail(e.target.value)}
                    className="flex-1"
                  />
                  <Button onClick={addRecipient} size="icon">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                
                {/* Recipients list */}
                <div className="space-y-2">
                  {alertConfig.recipients.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No hay destinatarios configurados. Se enviará a los superadmins por defecto.
                    </p>
                  ) : (
                    alertConfig.recipients.map((recipient, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                        <div>
                          <p className="font-medium">{recipient.name}</p>
                          <p className="text-sm text-muted-foreground">{recipient.email}</p>
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => removeRecipient(index)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
                
                <p className="text-xs text-muted-foreground">
                  Las alertas se envían por email cuando hay préstamos vencidos o próximos a vencer (7 días).
                </p>
              </CardContent>
            </Card>
          </div>
          
          {/* Bomberazo Alerts Section */}
          <Card className="border-red-500/30">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-red-500">
                  <AlertTriangle className="h-5 w-5" />
                  Alertas de Bomberazos (Salidas Sin Revisión)
                </CardTitle>
                <CardDescription>
                  Configura las notificaciones cuando el equipo sale sin revisión previa
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable/Disable */}
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="bomberazo_enabled"
                    checked={bomberazoConfig.enabled}
                    onCheckedChange={(checked) => setBomberazoConfig(prev => ({ ...prev, enabled: checked }))}
                  />
                  <Label htmlFor="bomberazo_enabled" className="cursor-pointer">
                    Activar alertas de bomberazos
                  </Label>
                </div>
                
                {bomberazoConfig.enabled && (
                  <>
                    {/* Notification Options */}
                    <div className="space-y-3">
                      <Label>Métodos de notificación</Label>
                      <div className="flex items-center gap-3">
                        <Checkbox
                          id="bomberazo_notification"
                          checked={bomberazoConfig.send_notification}
                          onCheckedChange={(checked) => setBomberazoConfig(prev => ({ ...prev, send_notification: checked }))}
                        />
                        <Label htmlFor="bomberazo_notification" className="cursor-pointer text-sm">
                          Centro de notificaciones (campanita)
                        </Label>
                      </div>
                      <div className="flex items-center gap-3">
                        <Checkbox
                          id="bomberazo_email"
                          checked={bomberazoConfig.send_email}
                          onCheckedChange={(checked) => setBomberazoConfig(prev => ({ ...prev, send_email: checked }))}
                        />
                        <Label htmlFor="bomberazo_email" className="cursor-pointer text-sm">
                          Correo electrónico
                        </Label>
                      </div>
                    </div>
                    
                    {/* Users to Notify */}
                    <div className="space-y-3">
                      <Label>Usuarios a notificar cuando hay bomberazo</Label>
                      <p className="text-xs text-muted-foreground">
                        Selecciona los usuarios que recibirán notificación inmediata cuando alguien saca equipo sin revisar
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                        {allUsers.filter(u => u.role === 'admin' || u.role === 'superadmin' || u.role === 'supervisor').map((user) => (
                          <div key={user.id} className="flex items-center gap-2">
                            <Checkbox
                              id={`notify_${user.id}`}
                              checked={bomberazoConfig.notify_user_ids.includes(user.id)}
                              onCheckedChange={() => toggleBomberazoUser(user.id)}
                            />
                            <Label htmlFor={`notify_${user.id}`} className="cursor-pointer text-sm flex items-center gap-2">
                              <span>{user.name}</span>
                              <Badge variant="outline" className="text-xs">{user.role}</Badge>
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Daily Report */}
                    <div className="border-t pt-4 space-y-3">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          id="daily_report_enabled"
                          checked={bomberazoConfig.daily_report_enabled}
                          onCheckedChange={(checked) => setBomberazoConfig(prev => ({ ...prev, daily_report_enabled: checked }))}
                        />
                        <Label htmlFor="daily_report_enabled" className="cursor-pointer">
                          Enviar reporte diario de bomberazos
                        </Label>
                      </div>
                      
                      {bomberazoConfig.daily_report_enabled && (
                        <>
                          <div className="flex items-center gap-3">
                            <Label className="text-sm">Hora del reporte:</Label>
                            <Input
                              type="time"
                              value={bomberazoConfig.daily_report_time}
                              onChange={(e) => setBomberazoConfig(prev => ({ ...prev, daily_report_time: e.target.value }))}
                              className="w-32"
                            />
                          </div>
                          
                          <div className="space-y-2">
                            <Label className="text-sm">Emails para reporte diario</Label>
                            <div className="flex gap-2">
                              <Input
                                placeholder="email@ejemplo.com"
                                value={newBomberazoEmail}
                                onChange={(e) => setNewBomberazoEmail(e.target.value)}
                                className="flex-1"
                              />
                              <Button variant="outline" onClick={addBomberazoReportEmail}>
                                <Plus className="h-4 w-4" />
                              </Button>
                            </div>
                            <div className="space-y-1 max-h-32 overflow-y-auto">
                              {bomberazoConfig.daily_report_recipients.map((email, index) => (
                                <div key={index} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                                  <span className="text-sm">{email}</span>
                                  <Button variant="ghost" size="icon" onClick={() => removeBomberazoReportEmail(index)}>
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}
                
                <div className="flex gap-2 pt-4">
                  <Button onClick={saveBomberazoConfig} disabled={savingBomberazoConfig} className="bg-red-600 hover:bg-red-700">
                    {savingBomberazoConfig ? 'Guardando...' : 'Guardar Configuración'}
                  </Button>
                  <Button variant="outline" onClick={testBomberazoReport}>
                    Probar Reporte
                  </Button>
                  <Button variant="outline" onClick={sendTestBomberazoPdfEmail} data-testid="test-bomberazo-pdf-email-btn">
                    <Mail className="h-4 w-4 mr-2" />
                    Probar PDF por correo
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      
      {/* Movement Details Modal */}
      <Dialog open={movementDetailsOpen} onOpenChange={setMovementDetailsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-primary" />
              Detalles del Movimiento
            </DialogTitle>
            <DialogDescription>
              Información completa del registro de salida
            </DialogDescription>
          </DialogHeader>
          
          {movementFullData ? (
            <div className="space-y-4 py-4">
              {/* General Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Evento/Destino</Label>
                  <p className="font-medium">{movementFullData.event_name}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">No. Control</Label>
                  <p className="font-mono">{movementFullData.control_number || 'Sin asignar'}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Cliente</Label>
                  <p>{movementFullData.client_name || '-'}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Contacto</Label>
                  <p>{movementFullData.client_contact || '-'}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Responsable</Label>
                  <p className="flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {movementFullData.responsible_name}
                  </p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Fecha/Hora Evento</Label>
                  <p className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {formatDate(movementFullData.event_date)} {movementFullData.event_time || ''}
                  </p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Fecha Registro</Label>
                  <p>{movementFullData.created_at ? formatDate(movementFullData.created_at) : '-'}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Estado</Label>
                  <Badge variant={movementFullData.has_return ? 'default' : 'warning'}>
                    {movementFullData.has_return ? 'Retornado' : 'Pendiente de retorno'}
                  </Badge>
                </div>
              </div>
              
              {/* Equipment List */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Equipos ({movementFullData.items?.length || 0})</Label>
                <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                  {movementFullData.items?.map((item, idx) => (
                    <div key={idx} className="p-3 flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-primary" />
                        <div>
                          <p className="font-medium">{item.equipment_name}</p>
                          <p className="text-xs text-muted-foreground">
                            Serial: {item.serial_numbers || 'Auto-asignado'} | Ubicación: {item.location_name || '-'}
                          </p>
                        </div>
                      </div>
                      <Badge variant="outline">x{item.quantity}</Badge>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Notes */}
              {movementFullData.notes && (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Notas</Label>
                  <p className="text-sm bg-muted/30 p-2 rounded">{movementFullData.notes}</p>
                </div>
              )}
              
              {/* Emergency/Bomberazo indicator */}
              {movementFullData.equipment_no_review && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <p className="text-sm text-red-400 font-medium flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Salida de Emergencia (Bomberazo) - Sin revisión previa
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              <RefreshCw className="h-6 w-6 mx-auto animate-spin mb-2" />
              Cargando detalles...
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Return Loan Modal */}
      <Dialog open={returnModalOpen} onOpenChange={setReturnModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-green-500" />
              Registrar Retorno de Préstamo
            </DialogTitle>
            <DialogDescription>
              {selectedLoan?.loan_reason || 'Préstamo'} - {selectedLoan?.responsible_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            {/* Items to return */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Equipos a retornar</Label>
              {returnData.items.map((item, idx) => (
                <div key={idx} className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-primary" />
                      <span className="font-medium">{item.equipment_name}</span>
                      {item.serial_numbers && (
                        <span className="text-xs text-muted-foreground font-mono">
                          ({item.serial_numbers})
                        </span>
                      )}
                    </div>
                    <Badge variant="outline">Cantidad: {item.quantity}</Badge>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {/* Quantity to return */}
                    <div className="space-y-1">
                      <Label className="text-xs">Cantidad a retornar</Label>
                      <Input
                        type="number"
                        min={0}
                        max={item.quantity}
                        value={item.return_quantity}
                        onChange={(e) => handleItemConditionChange(idx, 'return_quantity', parseInt(e.target.value) || 0)}
                        className="h-8"
                      />
                    </div>
                    
                    {/* Condition */}
                    <div className="space-y-1">
                      <Label className="text-xs">Condición</Label>
                      <Select
                        value={item.return_condition}
                        onValueChange={(val) => handleItemConditionChange(idx, 'return_condition', val)}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="BUENO">Bueno</SelectItem>
                          <SelectItem value="FALLA">Con Falla</SelectItem>
                          <SelectItem value="PERDIDO">Perdido</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Return location */}
                    <div className="space-y-1">
                      <Label className="text-xs">Ubicación destino</Label>
                      <Select
                        value={item.return_location_id}
                        onValueChange={(val) => handleItemConditionChange(idx, 'return_location_id', val)}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue placeholder="Seleccionar..." />
                        </SelectTrigger>
                        <SelectContent>
                          {locations.map(loc => (
                            <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Notes for faulty items */}
                  {item.return_condition === 'FALLA' && (
                    <div className="space-y-1">
                      <Label className="text-xs text-red-400">Descripción de la falla</Label>
                      <Input
                        placeholder="Describe el problema encontrado..."
                        value={item.notes || ''}
                        onChange={(e) => handleItemConditionChange(idx, 'notes', e.target.value)}
                        className="h-8 border-red-500/30"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* General notes */}
            <div className="space-y-2">
              <Label>Notas del retorno (opcional)</Label>
              <Textarea
                placeholder="Observaciones generales del retorno..."
                value={returnData.notes}
                onChange={(e) => setReturnData(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setReturnModalOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleProcessReturn}
              disabled={processingReturn || returnData.items.every(i => i.return_quantity === 0)}
              className="bg-green-600 hover:bg-green-700"
            >
              {processingReturn ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Confirmar Retorno
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Incident Details Modal */}
      <Dialog open={incidentModalOpen} onOpenChange={setIncidentModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedIncident?.status === 'perdido' && <Info className="h-5 w-5 text-red-600" />}
              {selectedIncident?.status === 'falla' && <AlertTriangle className="h-5 w-5 text-orange-500" />}
              {selectedIncident?.status === 'mantenimiento' && <Settings className="h-5 w-5 text-amber-600" />}
              Detalles del Incidente
            </DialogTitle>
            <DialogDescription>
              Información completa del equipo y el incidente
            </DialogDescription>
          </DialogHeader>
          
          {selectedIncident && (
            <div className="space-y-4">
              {/* Equipment Info */}
              <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-semibold">{selectedIncident.equipment_name}</span>
                  <Badge 
                    className={`${
                      ['perdido', 'faltante', 'perdido_recuperar', 'perdido_definitivo'].includes(selectedIncident.status) ? 'bg-red-600' :
                      ['falla', 'con_falla'].includes(selectedIncident.status) ? 'bg-orange-500' :
                      'bg-amber-600'
                    }`}
                  >
                    {selectedIncident.status === 'perdido' || selectedIncident.status === 'faltante' ? 'Perdido' :
                     selectedIncident.status === 'perdido_recuperar' ? 'Perdido (a recuperar)' :
                     selectedIncident.status === 'perdido_definitivo' ? 'Perdido definitivo' :
                     ['falla', 'con_falla'].includes(selectedIncident.status) ? 'Con Falla' :
                     selectedIncident.status === 'mantenimiento_interno' ? 'Mantenimiento interno' :
                     selectedIncident.status === 'mantenimiento_externo' ? 'Mantenimiento externo' :
                     'En Mantenimiento'}
                  </Badge>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Hash className="h-4 w-4" />
                  Serial: {selectedIncident.serial_number}
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  Ubicacion: {selectedIncident.location_name || selectedIncident.incident_details?.location_name || 
                    <span className="text-amber-500 font-medium">Sin ubicacion registrada</span>}
                </div>
              </div>
              
              {/* Fault Reason */}
              {selectedIncident.fault_reason && (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Razón de la Falla/Pérdida</Label>
                  <p className="p-3 bg-red-500/10 border border-red-500/30 rounded text-sm">
                    {selectedIncident.fault_reason}
                  </p>
                </div>
              )}
              
              {/* Incident Details */}
              {selectedIncident.incident_details ? (
                <div className="space-y-3">
                  <Label className="text-xs text-muted-foreground font-medium">Información del Evento</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {selectedIncident.incident_details.event_name && (
                      <div className="p-3 border rounded-lg">
                        <p className="text-xs text-muted-foreground">Evento</p>
                        {selectedIncident.incident_details.movement_id ? (
                          <button
                            type="button"
                            onClick={() => {
                              navigate(`/movements?open=${selectedIncident.incident_details.movement_id}`);
                            }}
                            className="font-medium text-primary hover:underline text-left"
                            data-testid="incident-modal-event-link"
                          >
                            {selectedIncident.incident_details.event_name} ↗
                          </button>
                        ) : (
                          <p className="font-medium">{selectedIncident.incident_details.event_name}</p>
                        )}
                      </div>
                    )}
                    {selectedIncident.incident_details.event_date && (
                      <div className="p-3 border rounded-lg">
                        <p className="text-xs text-muted-foreground">Fecha del Evento</p>
                        <p className="font-medium">{formatDate(selectedIncident.incident_details.event_date)}</p>
                      </div>
                    )}
                    {selectedIncident.incident_details.client_name && (
                      <div className="p-3 border rounded-lg">
                        <p className="text-xs text-muted-foreground">Cliente</p>
                        <p className="font-medium">{selectedIncident.incident_details.client_name}</p>
                      </div>
                    )}
                    {selectedIncident.incident_details.responsible_name && (
                      <div className="p-3 border rounded-lg border-blue-500/30" data-testid="incident-responsible">
                        <p className="text-xs text-muted-foreground">Responsable del Evento</p>
                        <p className="font-medium text-blue-400">{selectedIncident.incident_details.responsible_name}</p>
                      </div>
                    )}
                    {selectedIncident.incident_details.registered_by_name && (
                      <div className="p-3 border rounded-lg border-amber-500/30" data-testid="incident-registered-by">
                        <p className="text-xs text-muted-foreground">Registrado por</p>
                        <p className="font-medium text-amber-400">{selectedIncident.incident_details.registered_by_name}</p>
                      </div>
                    )}
                    {selectedIncident.incident_details.date && (
                      <div className="p-3 border rounded-lg">
                        <p className="text-xs text-muted-foreground">Fecha del Cambio</p>
                        <p className="font-medium">{formatDate(selectedIncident.incident_details.date)}</p>
                      </div>
                    )}
                  </div>
                  
                  {selectedIncident.incident_details.notes && (
                    <div className="p-3 bg-muted/30 rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">Notas/Comentarios</p>
                      <p className="text-sm">{selectedIncident.incident_details.notes}</p>
                    </div>
                  )}
                  
                  {selectedIncident.incident_details.reason && (
                    <div className="p-3 bg-muted/30 rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">Razón del Cambio</p>
                      <p className="text-sm">{selectedIncident.incident_details.reason}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-4 text-center text-muted-foreground bg-muted/20 rounded-lg">
                  <Info className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No hay información detallada del incidente original</p>
                  <p className="text-xs mt-1">El cambio de estado pudo haberse realizado manualmente</p>
                </div>
              )}
              
              {/* Last Update */}
              {selectedIncident.updated_at && (
                <div className="text-xs text-muted-foreground text-right">
                  Última actualización: {formatDate(selectedIncident.updated_at)}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EquipmentTrackingPage;
